import { useEffect, useState } from 'react'
import type { Frame } from '../../../shared/types'
import type { FigmaTransfer } from '../../../shared/figmaExport'
import { MAX_FIGMA_EXPORT_FRAMES } from '../../../shared/figmaExport'
import { errorMessage } from '../../lib/api'
import { FrameThumb } from '../../components/FramePreview'
import { Button } from '../../components/ui/button'
import { Disclosure } from '../../components/ui/collapsible'
import { Modal, ModalActions, ModalEyebrow, ModalLede, ModalTitle } from '../../components/ui/modal'
import { Note } from '../../components/ui/note'
import { Pressable } from '../../components/ui/pressable'
import { Step, Steps } from '../../components/ui/steps'
import { figmaApi } from './api'

const pluginUrl = import.meta.env.VITE_FIGMA_PLUGIN_URL as string | undefined

export function FigmaExportModal({ frames, onClose }: { frames: Frame[]; onClose: () => void }) {
  const [transfer, setTransfer] = useState<FigmaTransfer | null>(null)
  const [error, setError] = useState('')
  const [copyError, setCopyError] = useState('')
  const [copied, setCopied] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [setup, setSetup] = useState(false)
  const tooMany = frames.length > MAX_FIGMA_EXPORT_FRAMES

  useEffect(() => {
    if (!frames.length || tooMany) return
    const controller = new AbortController()
    // Defer work until after React's development effect replay has settled.
    const timer = window.setTimeout(() => {
      figmaApi
        .exportFrames(
          frames[0]!.canvasId,
          frames.map((frame) => frame.id),
          controller.signal,
        )
        .then(
          (result) => {
            if (!controller.signal.aborted) setTransfer(result)
          },
          (reason: unknown) => {
            if (!controller.signal.aborted) setError(errorMessage(reason, 'Couldn’t prepare these frames. Try again.'))
          },
        )
    }, 0)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [frames, attempt, tooMany])

  async function copy() {
    if (!transfer) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(transfer))
      setCopied(true)
      setCopyError('')
    } catch {
      setCopyError(
        'Your browser couldn’t copy these frames. Download the transfer file and choose it in the plugin instead.',
      )
    }
  }
  function download() {
    if (!transfer) return
    const url = URL.createObjectURL(new Blob([JSON.stringify(transfer)], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `${transfer.name.replace(/[^\p{L}\p{N} _-]/gu, '').slice(0, 80) || 'Doop frames'}.doop`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const count = frames.length
  return (
    <Modal onClose={onClose} size="md">
      <ModalEyebrow className="text-brand">Doop → Figma</ModalEyebrow>
      <ModalTitle className="mt-3">{copied ? 'Your next stop: Figma.' : 'Keep designing in Figma.'}</ModalTitle>
      <ModalLede>
        {copied
          ? 'Open the Doop plugin in your Figma file and paste. Then choose Add to page.'
          : 'Bring your frames over as editable text, shapes, vectors and images.'}
      </ModalLede>
      <div className="mt-5 flex gap-3 overflow-x-auto rounded-control border border-line bg-paper p-3">
        {frames.slice(0, 4).map((frame) => (
          <div key={frame.id} className="min-w-0 flex-1">
            <FrameThumb size="md" frameId={frame.id} />
            <p className="mt-2 truncate text-label font-medium">{frame.name}</p>
          </div>
        ))}
        {count > 4 && <span className="self-center text-label text-ink-soft">+{count - 4}</span>}
      </div>
      <div role="status" aria-live="polite" className="mt-3 text-label text-ink-soft">
        {tooMany
          ? `Select up to ${MAX_FIGMA_EXPORT_FRAMES} frames at a time.`
          : error
            ? error
            : transfer
              ? `${count} ${count === 1 ? 'frame' : 'frames'} · ${transfer.frames.reduce((total, frame) => total + frame.nodes.length, 0)} editable layers ready`
              : 'Preparing your layers and images…'}
      </div>
      <Steps className="mt-5">
        <Step>Copy your frames below.</Step>
        <Step>
          {pluginUrl ? (
            <>
              In Figma, run Plugins → “Doop — Editable import”.{' '}
              <a href={pluginUrl} target="_blank" rel="noreferrer" className="text-brand underline">
                Get plugin ↗
              </a>
            </>
          ) : (
            <>
              Open the Doop plugin in Figma.{' '}
              <Pressable layout="inline" focus="ring" onClick={() => setSetup(!setup)} className="text-brand underline">
                Set up plugin
              </Pressable>
            </>
          )}
        </Step>
        <Step>Paste, then add to your current page.</Step>
      </Steps>
      {setup && (
        <div className="mt-4 rounded-control border border-line bg-paper p-3 text-label leading-relaxed">
          <p className="font-semibold">One-time setup in Figma desktop (development build)</p>
          <p className="mt-1">
            <a className="underline" href="/figma-plugin/doop-figma-plugin.zip" download>
              Download the plugin
            </a>{' '}
            and unzip it. In Figma, choose Plugins → Development → Import plugin from manifest, then select
            manifest.json. Run “Doop — Editable import” from Development.
          </p>
        </div>
      )}
      {transfer && (
        <p className="mt-4 text-label leading-relaxed text-ink-soft">
          Layers keep their current positions. Auto Layout and reusable components aren’t generated. Missing fonts can
          be substituted in the plugin.
        </p>
      )}
      {!!transfer?.warnings.length && (
        <Disclosure
          label={`${transfer.warnings.length} ${transfer.warnings.length === 1 ? 'detail' : 'details'} to review in Figma`}
          className="mt-3 rounded-control border border-line p-3 text-label text-ink-soft"
          triggerClassName="font-medium text-ink"
        >
          <ul className="list-disc space-y-1 pl-4">
            {transfer.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Disclosure>
      )}
      {copyError && (
        <Note role="alert" tone="error" className="mt-3 block">
          {copyError}
        </Note>
      )}
      <ModalActions>
        <Button variant="bare" onClick={onClose}>
          {copied ? 'Done' : 'Cancel'}
        </Button>
        {error ? (
          <Button
            onClick={() => {
              setError('')
              setAttempt(attempt + 1)
            }}
          >
            Try again
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={download} disabled={!transfer}>
              Download file
            </Button>
            <Button variant="primary" onClick={() => void copy()} disabled={!transfer}>
              {copied ? 'Copy again' : 'Copy for Figma'}
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  )
}
